import { Alert, Box, Button, Paper, Stack, TextField, Typography } from '@mui/material';
import { plural, pluralWord } from '../../../lib/plural';
import { tripDateLabel } from '../../../lib/reminderPlan';
import { failureLine, outsidePhrase } from '../../../lib/tripDates';
import DateQuestion from '../dates/DateQuestion';
import ChangeNoticeDialog from '../changes/ChangeNoticeDialog';
import TripDatesMoveDialog from './TripDatesMoveDialog';
import { SET_ZONE_FIRST, useTripDates } from './useTripDates';
import type { GroupDetail } from '../../../types/groups';

/**
 * The trip's start and end dates, editable after the trip exists.
 *
 * They could only be typed on the create form, so a host who got a date wrong
 * (or whose trip moved) had no way to fix it, and nothing could ever trigger a
 * reminder plan's "Reschedule plan" (it appears when the trip's start date no
 * longer matches the one the plan was built for).
 *
 * Saved through groupToPayload for the reason that function documents (the update
 * REPLACES the record), from a group fetched fresh first so a co-host's newer story
 * or hero edit is not silently undone: the same pattern as TimeZoneCard. After a save,
 * none of which sends anything:
 *   1. when the START moved and a reminder plan is waiting on the old date, a note that
 *      the Broadcasts tab will offer "Reschedule plan";
 *   2. if the trip has itinerary events or dinners (and a trip time zone), an opt-in
 *      (TripDatesMoveDialog, everything unticked) to move them by the same number of days,
 *      which can be reopened from here later while events sit outside the dates;
 *   3. on a PUBLISHED trip, one change notice is OFFERED (ChangeNoticeDialog; "Don't send"
 *      is its default), folding in whatever moved.
 * Moving the start with the end untouched moves the end by the same amount, visibly, so the
 * trip does not silently shrink; the host can still set the end themselves. See lib/tripDates.
 */
export default function TripDatesCard({ group, onSaved }: { group: GroupDetail; onSaved: () => void }) {
  const {
    saved, draft, setStart, setEnd, resetDraft, endFollowed, busy, error, warning, moved, offer, plan, moveError, result, summary,
    notice, setNotice, dirty, errors, save, dateQuestion, confirmed, setConfirmed, canSave, moveSelected, retryFailed, openCatchUp, setOfferDays, finish, outside, zoneMissing,
  } = useTripDates(group, onSaved);

  return (
    <Paper sx={{ p: 3, borderRadius: 3, gridColumn: { md: '1 / -1' } }}>
      <Typography variant="h6" fontWeight={800}>Trip dates</Typography>
      <Typography color="text.secondary" variant="body2" sx={{ mt: 0.5 }}>
        When the trip starts and ends. Reminder plans are timed from the start date.
      </Typography>
      {error ? <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert> : null}
      {warning ? <Alert severity="warning" sx={{ mt: 2 }}>{warning}</Alert> : null}
      {moved ? (
        <Alert severity="info" sx={{ mt: 2 }}>
          The trip now starts {tripDateLabel(moved.to)} (it was {tripDateLabel(moved.from)}). Reminder plans on the
          Broadcasts tab that were made for the old date will offer &ldquo;Reschedule plan&rdquo;: nothing is rescheduled
          until you choose it there.
        </Alert>
      ) : null}
      {summary && summary.moved > 0 ? (
        <Alert severity="success" sx={{ mt: 2 }}>
          Moved {plural(summary.moved, 'item')} to the new dates, keeping their local times. Nobody was messaged.
        </Alert>
      ) : null}
      {summary && summary.failed.length > 0 ? (
        <Alert severity="warning" sx={{ mt: 2 }}>
          {plural(summary.failed.length, 'item')} could not be moved and {pluralWord(summary.failed.length, 'stays', 'stay')} on the old date:
          <Box component="ul" sx={{ m: 0, pl: 3 }}>
            {summary.failed.map((failure) => <li key={`${failure.move.kind}:${failure.move.id}`}>{failureLine(failure)}</li>)}
          </Box>
        </Alert>
      ) : null}
      {summary && summary.changed.length > 0 ? (
        <Alert severity="info" sx={{ mt: 2 }}>
          Left alone:
          <Box component="ul" sx={{ m: 0, pl: 3 }}>
            {summary.changed.map((failure) => <li key={`${failure.move.kind}:${failure.move.id}`}>{failureLine(failure)}</li>)}
          </Box>
        </Alert>
      ) : null}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 2, maxWidth: 520 }}>
        <TextField
          label="Start date"
          type="date"
          value={draft.start}
          disabled={busy}
          onChange={(event) => setStart(event.target.value)}
          InputLabelProps={{ shrink: true }}
          error={Boolean(errors.start)}
          helperText={errors.start}
          fullWidth
        />
        <TextField
          label="End date"
          type="date"
          value={draft.end}
          disabled={busy}
          onChange={(event) => setEnd(event.target.value)}
          InputLabelProps={{ shrink: true }}
          error={Boolean(errors.end)}
          helperText={errors.end ?? (endFollowed ? 'Moved with the start date so the trip keeps its length. Change it if that is not right.' : undefined)}
          fullWidth
        />
      </Stack>
      {dateQuestion ? (
        <DateQuestion
          question={dateQuestion}
          confirmed={confirmed === dateQuestion}
          onConfirm={(on) => setConfirmed(on ? dateQuestion : null)}
          disabled={busy}
          sx={{ mt: 2, maxWidth: 520 }}
        />
      ) : null}
      <Stack direction="row" spacing={1.5} sx={{ mt: 2 }}>
        <Button variant="contained" disabled={busy || !canSave} onClick={save}>
          {busy ? 'Saving…' : 'Save trip dates'}
        </Button>
        {dirty ? <Button variant="text" disabled={busy} onClick={resetDraft}>Cancel</Button> : null}
      </Stack>
      {outside.count > 0 && !offer ? (
        // Quiet on purpose: a rehearsal dinner the day before, or a brunch the day after, is normal, so this is information with a
        // link, not a warning to clear.
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }} data-testid="outside-dates-note">
          {outsidePhrase(outside)}{' '}
          {zoneMissing ? (
            <>
              {SET_ZONE_FIRST}{' '}
              <Button size="small" variant="text" disabled sx={{ textTransform: 'none', p: 0, minWidth: 0, verticalAlign: 'baseline' }}>
                Move events to match the trip dates
              </Button>
            </>
          ) : (
            <Button
              size="small"
              variant="text"
              disabled={busy}
              onClick={openCatchUp}
              sx={{ textTransform: 'none', p: 0, minWidth: 0, verticalAlign: 'baseline' }}
            >
              Move events to match the trip dates
            </Button>
          )}
        </Typography>
      ) : null}
      {offer && plan ? (
        <TripDatesMoveDialog
          plan={plan}
          offer={offer}
          busy={busy}
          error={moveError}
          result={result}
          onDays={offer.mode === 'catch_up' ? setOfferDays : undefined}
          onLeave={() => finish(null)}
          onConfirm={moveSelected}
          onRetry={retryFailed}
          onDone={() => finish(result)}
        />
      ) : null}
      <ChangeNoticeDialog open={Boolean(notice)} onClose={() => setNotice(null)} group={group} notice={notice} undoHint="change the dates back" />
    </Paper>
  );
}
